import { ProductTypeEnum } from "@kanpla/api-contract";
import { useMemo } from "react";

import { useUserProductsKiosk } from "~/api/products/hooks/use-user-products";
import { useShowPricesInclVat } from "~/hooks/use-show-prices-incl-vat";
import { computeBasket } from "~/lib/sale-engine";
import { logger } from "~/services/logger";
import { isProductAvailable } from "~/utils/order-deadlines/is-product-available";
import { calculateUserLimits } from "~/utils/user-limits/calculate-user-limits";
import { calculateAllowance } from "~/utils/allowance/calculate-allowance";
import { inferStockStatusFromBasket } from "~/utils/stock/infer-stock-status";

import { useKioskSession } from "../use-kiosk-session";

import { getUsedStampCards } from "./get-used-stampcards";
import {
  prepareKioskBasketLine,
  type PreparedKioskLine
} from "./prepare-kiosk-basket-line";
import { projectKioskLine } from "./project-kiosk-line";
import { parseDisplayProductList } from "./parse-display-product-list";
import { parseDisplayProductListRedeemable } from "./parse-display-product-list-redeemable";
import { parseOrderingProductBasket } from "./parse-ordering-product-basket";
import { sumKioskBasketTotals } from "./sum-kiosk-basket-totals";
import type {
  KioskDisplayBasketItem,
  KioskDisplayProductList,
  OrderingProductBasket,
  UseKioskProductsReturn
} from "./types";

export function useKioskProducts(): UseKioskProductsReturn {
  const { data: userProducts, status } = useUserProductsKiosk();
  const showPricesInclVat = useShowPricesInclVat();

  const basket = useKioskSession((session) => session?.basket ?? []);

  return useMemo(() => {
    if (status !== "success") {
      return {
        productsRedeemable: [],
        productsList: [],
        productsBasket: [],
        productsOrdering: [],
        categories: [],
        quantitySum: 0,
        unitTotalPriceInclVat: 0,
        unitTotalPriceExclVat: 0,
        unitTotalDepositInclVat: 0,
        unitTotalDepositExclVat: 0,
        unitTotalPayableInclVat: 0,
        unitTotalAllowanceDeductionInclVat: 0,
        showPricesInclVat,
        status,
        currency: "DKK",
        allowances: [],
        discounts: []
      };
    }

    const userLimits = calculateUserLimits({
      userLimits: userProducts.user?.userLimits ?? [],
      basket,
      products: userProducts.products
    });

    const allowances = calculateAllowance({
      allowances: userProducts.user?.allowances ?? [],
      discounts: userProducts.discounts,
      basket,
      products: userProducts.products
    });

    const productsList: KioskDisplayProductList[] = [];
    const productsRedeemable: KioskDisplayProductList[] = [];
    const productsBasket: KioskDisplayBasketItem[] = [];
    const productsOrdering: OrderingProductBasket[] = [];
    const prepared: PreparedKioskLine[] = [];

    const userLimitsMap = new Map(
      userLimits.map((userLimit) => [userLimit.id, userLimit])
    );

    for (const product of userProducts.products) {
      if (
        !isProductAvailable({
          deadline: product.deadline,
          onset: product.onset
        })
      ) {
        continue;
      }

      let isLimitOver = false;
      let remaining = null;
      if (product.userLimitId) {
        isLimitOver =
          userLimitsMap.get(product.userLimitId)?.isLimitOver ?? false;
        remaining = userLimitsMap.get(product.userLimitId)?.remaining ?? null;
      }

      const stockStatus = inferStockStatusFromBasket({
        product,
        basket
      });

      productsList.push(
        parseDisplayProductList({
          product,
          hybridBillingDiscounts: userProducts.discounts,
          allowances,
          isLimitOver,
          stockStatus,
          remaining,
          showPricesInclVat
        })
      );

      const productsInBasket = basket.filter(
        (item) => item.productId === product.id
      );
      for (const productInBasket of productsInBasket) {
        prepared.push(
          prepareKioskBasketLine({
            product,
            productInBasket,
            discounts: userProducts.discounts,
            stampCardDiscounts: userProducts.stampCardDiscounts,
            isLimitOver,
            stockStatus
          })
        );
      }

      if (
        userProducts?.stampCardDiscounts &&
        product.type !== ProductTypeEnum.WEIGHTED
      ) {
        const usedStampCardRuleIds = getUsedStampCards(basket);

        for (const stampCardDiscount of userProducts.stampCardDiscounts) {
          const ruleId = stampCardDiscount.stampCardRuleId;
          if (!ruleId || usedStampCardRuleIds.includes(ruleId)) continue;

          if (stampCardDiscount.productIds.includes(product.id)) {
            productsRedeemable.push(
              parseDisplayProductListRedeemable({
                product,
                stampCardDiscount,
                showPricesInclVat
              })
            );
          }
        }
      }
    }

    // One engine pass for the whole basket (mirrors POS). Kiosk has no order
    // discount.
    const priced = computeBasket(
      prepared.map((p) => p.engineLine),
      {
        allowances: userProducts.user?.allowances ?? [],
        discounts: userProducts.discounts
      }
    );
    const pricedByBasket = new Map(priced.map((p) => [p.basketId, p]));

    // Project + ordering payload, keyed by basketId (not index).
    for (const { meta, engineLine, product, productInBasket } of prepared) {
      const pricedLine = pricedByBasket.get(engineLine.basketId);
      if (!pricedLine) continue;

      const { item, allowanceLineTotal } = projectKioskLine(meta, pricedLine);
      productsBasket.push(item);

      productsOrdering.push(
        parseOrderingProductBasket({
          product,
          displayProductBasket: item,
          productInBasket,
          hybridBillingDiscounts: userProducts.discounts,
          stampCardDiscounts: userProducts.stampCardDiscounts,
          categories: userProducts.categories,
          allowanceLineTotal
        })
      );
    }

    for (const productBasket of productsBasket) {
      const exists = productsList.some(
        (productList) => productList.product.id === productBasket.product.id
      );

      if (!exists) {
        logger.warn("Products in basket not matching products list", {
          group_name: userProducts.user?.groupName,
          child_id: userProducts.user?.childId,
          product_id: productBasket.product.id
        });
      }
    }

    const quantitySum = productsBasket.reduce(
      (acc, item) => acc + item.quantity,
      0
    );

    const categories = userProducts.categories.filter((category) => {
      return productsList.some(
        (product) => product.product.categoryId === category.id
      );
    });

    return {
      categories,
      productsList,
      productsRedeemable,
      productsBasket,
      productsOrdering,
      quantitySum,
      ...sumKioskBasketTotals(productsBasket),
      showPricesInclVat,
      status,
      currency: userProducts.currency,
      allowances,
      discounts: userProducts.discounts
    };
  }, [userProducts, basket, status, showPricesInclVat]);
}
